<template>
  <SmartLinkItemComponent :id="model.system.id" :component="!model.system.workflow">
    <div :class="containerClass">
      <div :class="`container ${paddingOptions(data.paddingModifications)}`">
        <div v-if="data.isFullWidth" class="row">
          <div :class="getContentWidth()">
            <div class="header">
              <font-awesome v-if="data.icon" class="icon fa-fw" :icon="data.icon" />
              <SmartLinkStandaloneElement :codename="typ.elements.title.codename">
                <h2 v-if="data.title" class="title">
                  {{ data.title }}
                </h2>
              </SmartLinkStandaloneElement>
            </div>
            <SmartLinkStandaloneElement :codename="typ.elements.full_or_left_content.codename">
              <div class="content">
                <RichText :value="data.fullOrLeftContent" />
              </div>
            </SmartLinkStandaloneElement>
          </div>
        </div>
        <div v-else class="row">
          <div class="left-col" :class="leftSize">
            <div class="header">
              <font-awesome v-if="data.icon" class="icon fa-fw" :icon="data.icon" />
              <SmartLinkStandaloneElement :codename="typ.elements.title.codename">
                <h2 v-if="data.title" class="title">
                  {{ data.title }}
                </h2>
              </SmartLinkStandaloneElement>
            </div>
            <SmartLinkStandaloneElement :codename="typ.elements.full_or_left_content.codename">
              <RichText :value="data.fullOrLeftContent" />
            </SmartLinkStandaloneElement>
          </div>
          <div class="right-col" :class="rightSize">
            <div class="header">
              <font-awesome v-if="data.rightIcon" class="icon fa-fw" :icon="data.rightIcon" />
              <SmartLinkStandaloneElement :codename="typ.elements.right_title.codename">
                <h2 v-if="data.rightTitle" class="title">
                  {{ data.rightTitle }}
                </h2>
              </SmartLinkStandaloneElement>
            </div>
            <SmartLinkStandaloneElement :codename="typ.elements.right_content.codename">
              <RichText :value="data.rightContent" />
            </SmartLinkStandaloneElement>
          </div>
        </div>
      </div>
    </div>
  </SmartLinkItemComponent>
</template>

<script setup lang="ts">
import type { BasicContentBlockModel, CallToActionModel } from '~/models'
import { paddingOptions } from '~/utils/styling-options'

const props = defineProps<{ model: BasicContentBlockModel }>()
import { contentTypes } from '~/models'
const typ = contentTypes.basic_content_block

const data = {
  title: props.model.elements.title.value,
  fullOrLeftContent: props.model.elements.fullOrLeftContent.value,
  rightContent: props.model.elements.rightContent.value,
  rightTitle: props.model.elements.rightTitle.value,
  rightIcon: props.model.elements.rightTitleIcon.value,
  blockStyling: firstOrDefault(props.model.elements.blockStyling.value)?.codename,
  background: firstOrDefault(props.model.elements.background.value),
  mobileStyling: firstOrDefault(props.model.elements.mobileStyling.value),
  paddingModifications: props.model.elements.paddingModificationsPaddingModifications.value.map(
    (item) => item.codename
  ),
  icon: props.model.elements.icon.value,
  isFullWidth: isEmpty(props.model.elements.rightContent.value),
  cssClass: props.model.elements.cssClass.value
}

const blockStyling = ref(data.blockStyling)

function getBackground() {
  let cls = `content-block`

  if (data.background) {
    cls = `${cls} ${backgroundOptions(data.background.codename)}`
  }

  return cls
}

function getCustomCssClass() {
  let cls = ``

  if (data.cssClass) {
    cls = `${data.cssClass}`
  }

  return cls
}

function getContentWidth() {
  switch (data.blockStyling) {
    case typ.elements.block_styling.options.n60_40.codename:
      return 'col-lg-7'
    case typ.elements.block_styling.options.n40_60.codename:
      return 'col-lg-5'
    case typ.elements.block_styling.options.n50_50.codename:
      return 'col-lg-6'
    case typ.elements.block_styling.options.n70_30.codename:
      return 'col-lg-8'
    case typ.elements.block_styling.options.n30_70.codename:
      return 'col-lg-4'
    default:
      return 'col-lg-12'
  }
}

const rightContentOnTop = ref(false)
type BlockStyling =
  | 'style-60-40'
  | 'style-40-60'
  | 'style-50-50'
  | 'style-70-30'
  | 'style-30-70'
  | 'None'

const getBlockStyling = (): BlockStyling => {
  if (blockStyling?.value && blockStyling?.value?.length > 0) {
    const selectedStyle = props.model.elements.blockStyling.value[0].codename

    switch (selectedStyle) {
      case 'n60_40':
        return 'style-60-40'

      case 'n40_60':
        return 'style-40-60'

      case 'n50_50':
        return 'style-50-50'

      case 'n70_30':
        return 'style-70-30'

      case 'n30_70':
        return 'style-30-70'

      default:
        return 'None'
    }
  }

  return 'None'
}

const containerClass = computed(() => {
  return getBlockStyling() + ' ' + getBackground() + ' ' + getCustomCssClass()
})

const leftSize = computed(() => {
  const style = getBlockStyling()
  let classNames = ''

  switch (style) {
    case 'style-60-40':
      classNames += 'col-lg-7'
      break
    case 'style-40-60':
      classNames += 'col-lg-5'
      break
    case 'style-50-50':
      classNames += 'col-lg-6'
      break
    case 'style-70-30':
      classNames += 'col-lg-8'
      break
    case 'style-30-70':
      classNames += 'col-lg-4'
      break
    default:
      return ''
  }
  if (rightContentOnTop.value) {
    classNames += ' order-lg-1 order-2'
  }
  return classNames
})

const rightSize = computed(() => {
  const style = getBlockStyling()
  let classNames = ''

  switch (style) {
    case 'style-60-40':
      classNames += 'col-lg-5'
      break
    case 'style-40-60':
      classNames += 'col-lg-7'
      break
    case 'style-50-50':
      classNames += 'col-lg-6'
      break
    case 'style-70-30':
      classNames += 'col-lg-4'
      break
    case 'style-30-70':
      classNames += 'col-lg-8'
      break
    default:
      return ''
  }

  if (rightContentOnTop.value) {
    classNames += ' order-lg-2 order-1'
  }
  return classNames
})
</script>
